import { writePermissionRoles } from "@/const/GlobalConsts";
import { getLocaleSettings } from "@/utils/clientFormatUtils";
import { TimeZoneConsts } from "@/const/timeZones";

export class UserLoginInfo {
  id: string;
  adId: string;
  name: string;
  upn: string;
  mobilePhone: string;
  email: string;
  tenantInfo: TenantGlobalConfig | null;
  groups: GroupInfo[];
  teamsToken: string;
  isInTeams: boolean;
  setting: UserSetting | null;
  version: string;
  constructor(data?: Record<string, any>) {
    this.id = data?.id || "";
    this.adId = data?.adId || "";
    this.name = data?.name || "";
    this.upn = data?.upn || "";
    this.mobilePhone = data?.mobilePhone || "";
    this.email = data?.email || "";
    this.tenantInfo = new TenantGlobalConfig(data?.tenantInformation);

    const regularGroups = (data?.groups || []).map((group: any) =>
      group instanceof GroupInfo ? group : new GroupInfo(group)
    );

    const customGroups = (data?.customGroups || []).map(
      (group: any) => new GroupInfo({ ...group, IsCustomGroup: true })
    );

    this.groups = [...regularGroups, ...customGroups];

    this.isInTeams = data?.isInTeams || false;
    this.teamsToken = data?.teamsToken || "";
    this.setting = data?.setting ? new UserSetting(data.setting) : null;
    this.version = data?.version || "";
  }
}

export class UserSetting {
  id: number;
  defaultView: string;
  defaultZoomInTimeLine: string;
  categoryColors: Array<{ rowIndex: number; category: string; color?: string }>;
  language: string;
  timeScale: number;
  firstDayOfWeek: string;
  defaultGroup: string;
  groupOrder: string;
  displayGroup: string[];
  isOverrideReadAccess: boolean;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;

  constructor(data?: Record<string, any>) {
    this.id = data?.id || 0;
    this.defaultView = data?.defaultView || "";
    this.defaultZoomInTimeLine = data?.defaultZoomInTimeLine || "";
    this.categoryColors = data?.categoryColors || [];
    this.language = data?.language || "";
    this.timeScale = data?.timeScale || 30;
    this.firstDayOfWeek = data?.firstDayOfWeek || "sunday";
    this.defaultGroup = data?.defaultGroup || "";
    this.groupOrder = data?.groupOrder || "";
    this.displayGroup = data?.displayGroup || data?.groupIds || [];
    this.isOverrideReadAccess = data?.isOverrideReadAccess || false;
    this.dateFormat = data?.dateFormat || "";

    const excludedTimeFormats = ["HH:mm:ss", "H:mm:ss", "h:mm:ss A", "hh:mm:ss A", "hh:mm:ss a"];
    const rawTimeFormat = data?.timeFormat || "";
    if (rawTimeFormat && excludedTimeFormats.includes(rawTimeFormat)) {
      this.timeFormat = rawTimeFormat
        .replace("HH:mm:ss", "HH:mm")
        .replace("H:mm:ss", "H:mm")
        .replace("h:mm:ss A", "h:mm A")
        .replace("hh:mm:ss A", "hh:mm A")
        .replace("hh:mm:ss a", "hh:mm a");
    } else {
      this.timeFormat = rawTimeFormat;
    }
    this.timeZone = data?.timeZone || "";
  }

  getCategoryColor(category: string[]): string | undefined {
    const defaultColor = this.categoryColors.find((c) => c.category.toLowerCase() === "default");
    if (category.length === 0) {
      return defaultColor?.color;
    }

    if (category.length === 1) {
      const categoryColor = this.categoryColors.find(
        (c) => c.category.toLowerCase() === category[0].toLowerCase()
      );
      if (categoryColor) {
        return categoryColor.color;
      }
      return defaultColor?.color;
    } else {
      return (
        this.categoryColors.find((c) => c.category.toLowerCase() === "mixed")?.color ||
        defaultColor?.color
      );
    }
  }
}
export class TenantGlobalConfig {
  culture: string;
  dateFormat: string;
  timeFormat: string;
  dateTimeFormat: string;
  timeZone: string;
  region: string;
  tenantId?: string;
  tenantName?: string;

  constructor(data?: Record<string, string>) {
    const localeSettings = getLocaleSettings();
    this.culture = localeSettings.locale;
    this.dateFormat = localeSettings.dateFormat;
    this.timeFormat = localeSettings.timeFormat;
    this.dateTimeFormat = this.dateFormat + " " + this.timeFormat;

    const timezoneId = data?.timezone || "";
    if (timezoneId) {
      const timezone = TimeZoneConsts.find((tz) => tz.id === timezoneId);
      this.timeZone = timezone?.id || timezoneId;
    } else {
      this.timeZone = "";
    }

    this.region = data?.region || data?.culture || "";
    this.tenantId = data?.tenantId || "";
    this.tenantName = data?.tenantName || "";
  }
}

export class GroupInfo {
  id: string;
  adId: string;
  groupName: string;
  members: UserInfo[];
  isCustomGroup: boolean;
  constructor(data?: Record<string, any>) {
    this.id = data?.id || "";
    this.adId = data?.adId || "";
    this.groupName = data?.groupName || "";
    this.members = data?.members || [];
    this.isCustomGroup = data?.IsCustomGroup || false;
  }
}
export class UserInfo {
  id: string;
  name: string;
  adId: string;
  upn: string;
  email: string;
  color: string;
  groupId: string;
  isInsideOrganization?: boolean;
  permissionRole?: string;
  isInherited?: boolean;
  isRoleOverrided?: boolean;
  allowRoles?: string[];
  proxyAddresses?: string[];
  constructor(data?: Record<string, any>) {
    this.id = data?.groupId ? `${data.groupId}-${data?.id || ""}` : data?.id || "";
    this.name = data?.name || "";
    this.adId = data?.adId || "";
    this.upn = data?.upn || "";
    this.email = data?.email || "";
    this.groupId = data?.groupId || "";
    this.color = data?.color || "";
    this.isInsideOrganization = data?.isInsideOrganization || false;
    this.permissionRole = data?.permissionRole || "";
    this.allowRoles = data?.allowRoles || [];
    this.isInherited = data?.isInherited || false;
    this.isRoleOverrided = data?.isRoleOverrided || false;
    this.proxyAddresses = data?.proxyAddresses || [];
  }
}

export class UserSearchDto {
  id: string;
  displayName: string;
  email: string;
  userPrincipalName: string;
  proxyAddresses?: string[];
  constructor(data?: Record<string, any>) {
    this.id = data?.id || "";
    this.displayName = data?.displayName || "";
    this.email = data?.email || "";
    this.userPrincipalName = data?.userPrincipalName || "";
    this.proxyAddresses = data?.proxyAddresses || [];
  }
}

export const COLORFUL_BACKGROUNDS = [
  "#9B2226", // deep brick red
  "#BB3E03", // rustic orange
  "#CA6702", // golden amber
  "#AE2012", // bold crimson
  "#005F73", // dark teal
  "#0A9396", // cyan teal
  "#5A189A", // intense violet
  "#7B2CBF", // rich purple
  "#3C096C", // dark royal purple
  "#1B4332", // forest green
  "#2D6A4F", // emerald green
  "#081C15", // deep greenish black
  "#003049", // deep midnight blue
  "#1A759F", // azure blue
  "#370617", // deep wine red
  "#9D0208", // strong scarlet
  "#B5838D", // muted rose
  "#772026", // dark raspberry
  "#5C2A9D", // indigo purple
  "#0F4C5C", // deep cyan blue
  "#BC4749", // terra cotta red
  "#6A040F", // burgundy wine
  "#E63946", // vibrant crimson
  "#2A9D8F", // turquoise teal
  "#264653", // midnight slate
  "#287271", // deep sea green
  "#8B2635", // ruby red
  "#1D3557", // naval blue
  "#A63446", // rose crimson
  "#6D2E46", // plum wine
  "#0D3B66", // prussian blue
  "#6D466B", // dusty purple
  "#843B62", // magenta plum
  "#A64253", // raspberry rose
  "#2C6E49", // hunter green
  "#4C956C", // sage forest
  "#5F0F40", // deep magenta
  "#9A031E", // cardinal red
  "#0B3954", // deep ocean blue
  "#087E8B", // tropical teal
  "#C1121F", // fire engine red
  "#780000", // maroon depth
  "#003566", // sapphire blue
  "#6A4C93", // royal lavender
  "#1B998B", // jade green
  "#8A817C", // warm stone
  "#663399", // rebecca purple
  "#8B4513", // saddle brown
  "#2F4F4F", // dark slate gray
  "#8B0000" // dark red
];

export function generateRandomHexColor(): string {
  return COLORFUL_BACKGROUNDS[Math.floor(Math.random() * COLORFUL_BACKGROUNDS.length)];
}

export function generateColorFromId(id: string): string {
  const normalizedId = (id || "").trim().toLowerCase();

  if (!normalizedId) {
    return COLORFUL_BACKGROUNDS[0];
  }

  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  const index = Math.abs(hash) % COLORFUL_BACKGROUNDS.length;
  return COLORFUL_BACKGROUNDS[index];
}
